import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  load_stock: { label: { it: "Carico", en: "Load" }, cls: "bg-emerald-100 text-emerald-800" },
  unload_stock: { label: { it: "Scarico", en: "Unload" }, cls: "bg-sky-100 text-sky-800" },
  waste: { label: { it: "Spreco", en: "Waste" }, cls: "bg-orange-100 text-orange-800" },
  temperature: { label: { it: "Temperatura", en: "Temperature" }, cls: "bg-cyan-100 text-cyan-800" },
};

export function AiAssistantPanel({ onDone, embedded = false }) {
  const { locationId } = useAuth();
  const { t, lang } = useLang();
  const [text, setText] = useState("");
  const [proposals, setProposals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [executing, setExecuting] = useState(false);

  const interpret = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setProposals(null);
    try {
      const r = await api.post("/ai/interpret", { text, location_id: locationId });
      if (!r.data.proposals.length) {
        toast.error(lang === "it" ? "Nessuna azione riconosciuta" : "No action recognized");
      } else {
        setProposals(r.data.proposals);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const updateProposal = (i, k, v) => {
    setProposals((prev) => prev.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const r = await api.post("/ai/execute", { actions: proposals, location_id: locationId });
      r.data.results.forEach((res) => (res.ok ? toast.success(res.message) : toast.error(res.message)));
      setProposals(null);
      setText("");
      window.dispatchEvent(new CustomEvent("sk:data-changed"));
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setExecuting(false);
  };

  return (
    <div data-testid="ai-assistant-card"
      className={embedded ? "space-y-3" : "bg-card border-2 border-sky-200 rounded-2xl p-4 sm:p-5 space-y-3 shadow-sm"}>
      {!embedded && (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <h2 className="font-display font-bold">Assistente AI</h2>
        </div>
      )}
      <form onSubmit={interpret} className="flex gap-2">
        <input
          data-testid="ai-assistant-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("dash.aiPlaceholder")}
          className="flex-1 h-14 rounded-xl border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button data-testid="ai-assistant-submit" type="submit" disabled={busy || !text.trim()}
          className="h-14 px-5 tactile-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </form>
      {busy && <p className="text-xs text-muted-foreground">{t("dash.aiInterpreting")}</p>}

      {proposals && (
        <div className="border-t pt-3 space-y-3 animate-fade-up" data-testid="ai-proposals">
          <p className="text-sm font-semibold">{t("dash.aiProposal")}</p>
          <div className="space-y-2">
            {proposals.map((p, i) => {
              const meta = TYPE_META[p.type] || TYPE_META.load_stock;
              return (
                <div key={i} data-testid={`ai-proposal-${i}`}
                  className="flex flex-wrap items-center gap-2 bg-secondary/60 rounded-xl p-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${meta.cls}`}>
                    {meta.label[lang]}
                  </span>
                  <span className="font-semibold text-sm">{p.product_name || p.equipment_name}</span>
                  {!p.matched && p.type !== "temperature" && (
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-amber-100 text-amber-800">
                      {lang === "it" ? "nuovo prodotto" : "new product"}
                    </span>
                  )}
                  {p.type === "temperature" ? (
                    <div className="flex items-center gap-1">
                      <input data-testid={`ai-proposal-temp-${i}`} type="number" step="0.1"
                        value={p.temperature ?? ""} onChange={(e) => updateProposal(i, "temperature", Number(e.target.value))}
                        className="w-20 h-9 rounded-lg border bg-background px-2 text-sm font-mono" />
                      <span className="font-mono text-sm">°C</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input data-testid={`ai-proposal-qty-${i}`} type="number" step="0.01" min="0"
                        value={p.quantity ?? ""} onChange={(e) => updateProposal(i, "quantity", Number(e.target.value))}
                        className="w-20 h-9 rounded-lg border bg-background px-2 text-sm font-mono" />
                      <span className="text-xs text-muted-foreground">{p.unit}</span>
                    </div>
                  )}
                  {p.type === "waste" && (
                    <select data-testid={`ai-proposal-reason-${i}`} value={p.reason || "altro"}
                      onChange={(e) => updateProposal(i, "reason", e.target.value)}
                      className="h-9 rounded-lg border bg-background px-2 text-xs">
                      {Object.keys(t("waste.reasons")).map((r) => (
                        <option key={r} value={r}>{t(`waste.reasons.${r}`)}</option>
                      ))}
                    </select>
                  )}
                  {p.estimated_cost != null && (
                    <span className="text-xs font-mono font-bold text-orange-700">≈ €{p.estimated_cost.toFixed(2)}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button data-testid="ai-confirm-button" onClick={execute} disabled={executing}
              className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 tactile-btn font-bold">
              {executing ? <Loader2 className="h-5 w-5 animate-spin" /> : t("dash.confirm")}
            </Button>
            <Button data-testid="ai-cancel-button" variant="outline" onClick={() => setProposals(null)}
              className="h-12 px-6 tactile-btn">
              <X className="h-4 w-4 mr-1" />{t("dash.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function FloatingAiAssistant() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="floating-ai-button" onClick={() => setOpen(true)}
        title="Assistente AI"
        className="tactile-btn fixed bottom-24 right-4 md:bottom-8 md:right-8 z-50 h-14 w-14 rounded-2xl bg-primary text-white shadow-xl flex items-center justify-center hover:scale-105">
        <Sparkles className="h-6 w-6" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="floating-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />Assistente AI
            </DialogTitle>
          </DialogHeader>
          <AiAssistantPanel embedded onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
